// OTHER PEOPLES ON THE ISLAND. Matt, 2026-09-21: "we need to introduce the
// idea of other civilizations that we're coming across. So not just these
// random raiders, but civilizations that also have technologies and their
// own motivations... I want to seed the world with those and have them act
// and be actual adversaries competing for goals."
//
// A rival is a SEAT on the map, the land it has claimed around that seat,
// a POWER (its strength when fought, like a unit's), a LEVEL (its
// technology - the raiders it sends are that much stronger) and a TEMPER:
// hostile, wary or friendly. Every night it grows a little, claims a little
// more ground, and does what its temper says: a hostile people sends raiders
// and makes demands; a friendly one sends carts and shares what its
// scholars know; a wary one watches. Finds on the ground it takes are gone -
// that is the competing. The Frontier can raid it (an exchange, like a camp)
// or parley with it (coin); the Hall can send an Envoy; the world deals the
// meeting as a card. Pure helpers over `civ.rivals`; the night's moves live
// in game.js and call these.

import { RIVAL, RIVAL_NAMES, STANCES } from './constants';
import {
  key as hexKey, unkey, within, neighbours, hexDistance, hexDistanceBetween, siteAt, isTown, baseTerrain, isWater,
} from './map';
import { nextInt, shuffle } from './rng/rng';

export const rivalsOf = (civ) => civ?.rivals || [];
/** A people's colour, from the name table (a rival object carries only its id). */
export const rivalColour = (idOrRival) => RIVAL_NAMES.find((n) => n.id === (idOrRival?.id || idOrRival))?.colour || '#9a9a9a';
export const rivalBlurb = (idOrRival) => RIVAL_NAMES.find((n) => n.id === (idOrRival?.id || idOrRival))?.blurb || '';
export const rivalById = (civ, id) => rivalsOf(civ).find((r) => r.id === id) || null;
/** What makes this people itself: RIVAL's dials, with its own way on top. */
export const wayOf = (idOrRival) => {
  const n = RIVAL_NAMES.find((x) => x.id === (idOrRival?.id || idOrRival));
  return {
    growEvery: RIVAL.growEvery,
    expandEvery: RIVAL.expandEvery,
    maxHeld: RIVAL.maxHeld,
    tradeEvery: RIVAL.tradeEvery,
    trade: RIVAL.trade,
    startLevel: 0,
    demandsWhenWary: false,
    slowAnger: false,
    warmBy: 1,
    parleyHalf: false,
    ...(n?.way || {}),
  };
};
export const wayText = (idOrRival) => RIVAL_NAMES.find((n) => n.id === (idOrRival?.id || idOrRival))?.wayText || '';
/** Every hex any rival holds, keyed, -> rival id. */
export const rivalGround = (civ) => {
  const out = new Map();
  rivalsOf(civ).forEach((r) => (r.held || []).forEach((k) => out.set(k, r.id)));
  return out;
};
export const rivalAt = (civ, q, r) => {
  const id = rivalGround(civ).get(hexKey(q, r));
  return id ? rivalById(civ, id) : null;
};
export const isRivalGround = (civ, q, r) => rivalGround(civ).has(hexKey(q, r));
/** Rivals a unit standing here could raid or parley with: any whose ground is adjacent. */
export const rivalsNear = (civ, unit) => {
  if (!unit) return [];
  const ground = rivalGround(civ);
  const ids = new Set();
  neighbours(unit.q, unit.r).forEach(([q, r]) => { const id = ground.get(hexKey(q, r)); if (id) ids.add(id); });
  return [...ids].map((id) => rivalById(civ, id)).filter(Boolean);
};
export const stanceWord = (s) => ({ hostile: 'hostile', wary: 'wary', friendly: 'friendly' })[s] || s;
/** One step warmer or colder. */
export const warmer = (s) => STANCES[Math.min(STANCES.length - 1, STANCES.indexOf(s) + 1)];
export const colder = (s) => STANCES[Math.max(0, STANCES.indexOf(s) - 1)];
/** What a hostile rival adds to the threat readout. */
export const rivalThreat = (civ) => rivalsOf(civ).filter((r) => r.stance === 'hostile').reduce((n, r) => n + r.power, 0);

/** Ground a rival may claim: land, ring 3 out, not a site, not the town, not held, not a village of ours. */
const claimable = (civ, q, r) => {
  if (hexDistance(q, r) < 3) return false;
  if (isTown(q, r) || siteAt(q, r, civ) >= 0) return false;
  if (isWater(baseTerrain(q, r, civ.mapSeed || 0))) return false;
  if (rivalGround(civ).has(hexKey(q, r))) return false;
  if ((civ.villages || []).some((v) => v.q === q && v.r === r)) return false;
  if ((civ.menaces || []).some((m) => m.q === q && m.r === r)) return false;
  return true;
};

/** The nearest people not yet met, to the hex given (the Neighbours region names them). */
export const nearestUnmet = (civ, q, r) => rivalsOf(civ)
  .filter((x) => !x.met)
  .sort((a, b) => hexDistanceBetween([a.q, a.r], [q, r]) - hexDistanceBetween([b.q, b.r], [q, r]))[0] || null;

/**
 * Seat the rivals at the start: far from the town, far from each other, on
 * claimable ground. Returns [rivals, rng].
 */
export const seedRivals = (civ, rng) => {
  let names;
  [names, rng] = shuffle(RIVAL_NAMES, rng);
  const spots = [];
  // Six to nine out, on the home island's land: met in the first year or
  // two, as before, wherever the coast falls.
  within(0, 0, 9).forEach(([q, r]) => { if (hexDistance(q, r) >= 6 && claimable(civ, q, r)) spots.push([q, r]); });
  const rivals = [];
  for (let i = 0; i < RIVAL.count && names[i]; i += 1) {
    const free = spots.filter(([q, r]) => rivals.every((x) => hexDistanceBetween([x.q, x.r], [q, r]) >= 5));
    const pool = free.length ? free : spots;
    if (!pool.length) break;
    let at;
    [at, rng] = nextInt(pool.length, rng);
    const [q, r] = pool[at];
    rivals.push({
      id: names[i].id,
      name: names[i].name,
      q,
      r,
      held: [hexKey(q, r)],
      power: RIVAL.startPower,
      level: wayOf(names[i].id).startLevel,
      stance: 'wary',
      age: 0,
      met: false,
      broken: 0,
    });
  }
  return [rivals, rng];
};

/** The next hex this rival would take: the claimable one nearest its seat, ties toward the town. */
export const nextClaim = (civ, rival) => {
  const ground = rivalGround(civ);
  const edge = new Map();
  (rival.held || []).forEach((k) => {
    const [q, r] = unkey(k);
    neighbours(q, r).forEach(([a, b]) => {
      const kk = hexKey(a, b);
      if (ground.has(kk) || edge.has(kk)) return;
      if (!claimable(civ, a, b)) return;
      edge.set(kk, [a, b]);
    });
  });
  let best = null;
  edge.forEach(([q, r]) => {
    // Nearest their seat first; among those, the ground FURTHEST from the
    // town, so they spread along the shore before they press inward.
    const d = hexDistanceBetween([q, r], [rival.q, rival.r]) * 10 - hexDistance(q, r);
    if (!best || d < best.d) best = { q, r, d };
  });
  return best;
};

/** Where this rival's raiders come from: its held hex nearest the town. */
export const raidFrom = (civ, rival) => {
  let best = null;
  (rival.held || []).forEach((k) => {
    const [q, r] = unkey(k);
    const d = hexDistance(q, r);
    if (!best || d < best.d) best = { q, r, d };
  });
  return best;
};

/** Have we seen any of their ground? */
export const inSight = (civ, rival) => {
  const seen = new Set(civ.seen || []);
  return (rival.held || []).some((k) => seen.has(k));
};

export const describeRival = (r) => `${r.name} (${stanceWord(r.stance)}, strength ${r.power}${r.level ? `, level ${r.level}` : ''})`;
